import { UseChatHelpers, type UIMessage } from '@ai-sdk/react';
import { useEffect, useRef } from 'react';

const dedupeMessagesById = <T extends UIMessage>(history: T[]) => {
  const seenIds = new Set<string>();

  return history.filter((message) => {
    if (seenIds.has(message.id)) {
      return false;
    }

    seenIds.add(message.id);
    return true;
  });
};

interface UseChatHistoryHydrationProps<T extends UIMessage> {
  sessionKey: string;
  enabled: boolean;
  history: T[] | null | undefined;
  messages: T[];
  setMessages: UseChatHelpers<T>['setMessages'];
}

export const useChatHistoryHydration = <T extends UIMessage>({
  sessionKey,
  enabled,
  history,
  messages,
  setMessages,
}: UseChatHistoryHydrationProps<T>) => {
  const hasHydratedRef = useRef(false);

  useEffect(() => {
    hasHydratedRef.current = false;
    setMessages([]);
  }, [sessionKey, setMessages]);

  useEffect(() => {
    const sourceMessages = history ?? [];
    const shouldHydrate = enabled && !hasHydratedRef.current && messages.length === 0 && sourceMessages.length > 0;

    if (!shouldHydrate) {
      return;
    }

    setMessages(dedupeMessagesById(sourceMessages));
    hasHydratedRef.current = true;
  }, [enabled, history, messages.length, sessionKey, setMessages]);
};
